import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Receipt, Check, X, Edit2, Trash2, DollarSign } from 'lucide-react';
import { ReceiptUpload } from './ReceiptUpload';

interface Expense {
  id: string;
  created_at: string;
  charge_date: string;
  amount: number;
  merchant: string;
  category: string;
  receipt_url: string;
}

export function ExpenseReport() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Expense>>({});

  async function fetchExpenses() {
    setLoading(true);
    try {
      if (!supabase) {
        setExpenses([]);
        return;
      }

      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('charge_date', { ascending: false });

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchExpenses();
  }, []);

  const startEditing = (expense: Expense) => {
    setEditingId(expense.id);
    setEditForm({
      merchant: expense.merchant,
      amount: expense.amount,
      charge_date: expense.charge_date.split('T')[0], // Format date for input
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleUpdate = async (id: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('expenses')
        .update({
          merchant: editForm.merchant,
          amount: editForm.amount,
          charge_date: new Date(editForm.charge_date || '').toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      await fetchExpenses();
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating expense:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!supabase) return;
    
    try {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  if (loading) {
    return <div className="text-center">Loading expenses...</div>;
  }

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Upload Receipt
        </h2>
        <ReceiptUpload onUploadSuccess={fetchExpenses} />
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Recent Expenses</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-indigo-50 px-3 py-1 rounded-full">
              <DollarSign className="h-4 w-4 text-indigo-600" />
              <span className="text-indigo-700 font-medium">
                Total: ${expenses.reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}
              </span>
            </div>
            {!supabase && (
              <span className="text-sm text-amber-600">⚠️ Supabase not connected</span>
            )}
          </div>
        </div>
        <div>
          {expenses.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <Receipt className="mx-auto h-12 w-12 mb-4" />
              <p>{!supabase 
                ? 'Please connect to Supabase to start tracking expenses'
                : 'No expenses found. Upload a receipt to get started!'}</p>
            </div>
          ) : (
            expenses.map((expense, index) => (
              <div 
                key={expense.id} 
                className={`px-6 py-4 flex items-center justify-between ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                <div className="grid grid-cols-5 gap-6 w-full items-center">
                  <div>
                    {editingId === expense.id ? (
                      <input
                        type="date"
                        value={editForm.charge_date}
                        onChange={(e) =>
                          setEditForm({ ...editForm, charge_date: e.target.value })
                        }
                        className="w-full px-2 py-1 border rounded text-gray-900"
                      />
                    ) : (
                      <span className="text-gray-600">
                        {new Date(expense.charge_date).toISOString().split('T')[0].replace(/(\d{4})-(\d{2})-(\d{2})/, '$2-$3-$1')}
                      </span>
                    )}
                  </div>
                  <div>
                    {editingId === expense.id ? (
                      <input
                        type="text"
                        value={editForm.merchant}
                        onChange={(e) => setEditForm({ ...editForm, merchant: e.target.value })}
                        className="w-full px-2 py-1 border rounded text-gray-900"
                      />
                    ) : (
                      <span className="text-gray-900">{expense.merchant}</span>
                    )}
                  </div>
                  <div className="text-right">
                    {editingId === expense.id ? (
                      <input
                        type="number"
                        value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })}
                        step="0.01"
                        min="0"
                        className="w-full px-2 py-1 border rounded text-gray-900 text-right font-medium"
                        style={{ fontFamily: 'inherit' }}
                      />
                    ) : (
                      <span className="text-gray-900 font-medium">
                        ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                  <div className="text-center">
                    {expense.receipt_url && (
                      <div className="group relative">
                        <a
                          href={expense.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          View Receipt
                        </a>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute z-10 p-2 bg-white rounded-lg shadow-lg -translate-x-1/2 left-1/2 bottom-full mb-2 pointer-events-none">
                          <img
                            src={expense.receipt_url}
                            alt="Receipt preview"
                            className="max-w-xs max-h-48 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><line x1="22" y1="6" x2="2" y2="6"/></svg>';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    {editingId === expense.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdate(expense.id)}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditing(expense)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-1 text-red-400 hover:text-red-600 transition-colors"
                          title="Delete expense"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}